/**
 * Escucha el gesto de scroll del usuario sobre el hilo de conversación y avisa
 * en cuanto quiere SUBIR — antes de que el browser mueva el scroll.
 *
 * Es la capa 1 del autoscroll respetuoso (la 2 es la deriva del `scrollTop`,
 * en `threadPin.ts`). Existe por timing: mientras el coach habla, el hilo se
 * pega al fondo en cada frame, así que detectar la intención DESPUÉS de que el
 * scroll ya se movió deja siempre uno o dos frames en los que el hilo va y
 * vuelve — se siente como fricción y como si el último recuadro vibrara.
 * `wheel` y `touchmove` se disparan ANTES del scroll: soltando ahí, el loop
 * nunca llega a pelear.
 *
 * Todos los listeners son PASIVOS: escuchamos, no cancelamos el scroll.
 */

/** Sin eventos de rueda por este rato damos el gesto por terminado (la rueda no avisa su final). */
const WHEEL_IDLE_MS = 180;
/** Píxeles de arrastre del dedo hacia abajo que cuentan como "quiero subir" (y no como un tap). */
const TOUCH_DRAG_PX = 4;

export interface ThreadGestureHandlers {
  /** El usuario empezó a subir: hay que soltar el hilo YA. */
  onUpIntent: () => void;
  /**
   * El gesto terminó (dedo levantado / la rueda dejó de emitir). Recién acá
   * tiene sentido reevaluar el re-enganche: hacerlo durante el gesto devolvería
   * el hilo al fondo justo mientras el usuario empuja, porque los primeros
   * píxeles de un gesto que arranca desde el fondo caen dentro del margen de
   * re-enganche.
   */
  onGestureEnd: () => void;
}

/** Cablea los listeners y devuelve el detach. */
export function attachThreadGestures(el: HTMLElement, h: ThreadGestureHandlers): () => void {
  const win = el.ownerDocument.defaultView ?? window;
  let idleTimer = 0;
  let touchStartY = 0;
  let touchReleased = false;

  const endSoon = (): void => {
    win.clearTimeout(idleTimer);
    idleTimer = win.setTimeout(() => {
      idleTimer = 0;
      h.onGestureEnd();
    }, WHEEL_IDLE_MS);
  };

  const onWheel = (e: WheelEvent): void => {
    if (e.deltaY >= 0) return; // bajar no suelta el hilo: para eso está el re-enganche
    h.onUpIntent();
    endSoon();
  };

  const onTouchStart = (e: TouchEvent): void => {
    touchStartY = e.touches[0]?.clientY ?? 0;
    touchReleased = false;
  };

  const onTouchMove = (e: TouchEvent): void => {
    if (touchReleased) return;
    const y = e.touches[0]?.clientY;
    if (y === undefined) return;
    // Dedo BAJANDO = contenido subiendo = el usuario quiere ver lo de arriba.
    if (y - touchStartY <= TOUCH_DRAG_PX) return;
    touchReleased = true;
    h.onUpIntent();
  };

  const onTouchEnd = (): void => {
    if (!touchReleased) return;
    touchReleased = false;
    // El dedo sí avisa su final: no hace falta esperar el timer.
    win.clearTimeout(idleTimer);
    idleTimer = 0;
    h.onGestureEnd();
  };

  const opts = { passive: true } as const;
  el.addEventListener('wheel', onWheel, opts);
  el.addEventListener('touchstart', onTouchStart, opts);
  el.addEventListener('touchmove', onTouchMove, opts);
  el.addEventListener('touchend', onTouchEnd, opts);
  el.addEventListener('touchcancel', onTouchEnd, opts);

  return () => {
    win.clearTimeout(idleTimer);
    el.removeEventListener('wheel', onWheel);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchmove', onTouchMove);
    el.removeEventListener('touchend', onTouchEnd);
    el.removeEventListener('touchcancel', onTouchEnd);
  };
}

/** Tras la última acción que mueve el scroll, cuánto la seguimos contando (un PageUp suave dura ~0,5 s). */
const SCROLL_INPUT_MS = 800;
const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End', ' ']);

export interface ScrollInputTracker {
  /** ¿El usuario está moviendo (o acaba de mover) el scroll del hilo? */
  active: () => boolean;
  detach: () => void;
}

/**
 * Lo que puede mover el scroll del hilo de parte del usuario: la rueda, el dedo
 * arrastrando, las teclas de scroll y la barra apretada (un `pointerdown` sobre
 * el contenedor mismo, no sobre su contenido: un toque en un botón no scrollea).
 * Con esto `reducePin` distingue al usuario de un recorte del browser
 * (`ReducePinOptions.scrollInput`).
 */
export function trackScrollInput(el: HTMLElement): ScrollInputTracker {
  const win = el.ownerDocument.defaultView ?? window;
  let at = -Infinity;
  let held = false;
  const mark = (): void => {
    at = win.performance.now();
  };
  const onPointerDown = (e: PointerEvent): void => {
    if (e.target === el) held = true;
  };
  const onPointerUp = (): void => {
    if (!held) return;
    held = false;
    mark();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (SCROLL_KEYS.has(e.key)) mark();
  };
  const opts = { passive: true } as const;
  el.addEventListener('wheel', mark, opts);
  el.addEventListener('touchmove', mark, opts);
  el.addEventListener('pointerdown', onPointerDown, opts);
  win.addEventListener('pointerup', onPointerUp, opts);
  win.addEventListener('pointercancel', onPointerUp, opts);
  win.addEventListener('keydown', onKey, opts);
  return {
    active: () => held || win.performance.now() - at < SCROLL_INPUT_MS,
    detach: () => {
      el.removeEventListener('wheel', mark);
      el.removeEventListener('touchmove', mark);
      el.removeEventListener('pointerdown', onPointerDown);
      win.removeEventListener('pointerup', onPointerUp);
      win.removeEventListener('pointercancel', onPointerUp);
      win.removeEventListener('keydown', onKey);
    },
  };
}
